<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useSidebar } from 'dashboard/composables/useSidebar';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuItem,
} from 'reka-ui';
import Icon from 'next/icon/Icon.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const { isMobile } = useSidebar();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <div class="grid gap-0 px-1.5">
    <!-- Availability Status Dropdown -->
    <DropdownMenuRoot>
      <DropdownMenuTrigger as-child>
        <button
          class="flex items-center justify-between w-full rounded-md px-2.5 py-2 text-[15px] hover:bg-n-alpha-2 cursor-pointer"
          @click.stop
        >
          <div class="flex-grow flex items-center gap-1.5">
            {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
          </div>
          <div class="flex gap-1.5 items-center min-w-[96px] justify-end">
            <div class="p-1.5 flex-shrink-0">
              <div class="size-2.5 rounded-sm" :class="activeStatus.color" />
            </div>
            <span class="text-[15px]">{{ activeStatus.label }}</span>
            <Icon
              icon="i-lucide-chevron-down"
              class="size-[18px] flex-shrink-0"
            />
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent
          :side="isMobile ? 'top' : 'right'"
          align="end"
          :side-offset="4"
          class="min-w-32 rounded-lg bg-n-solid-1 border border-n-weak p-1.5 shadow-lg z-[10000]"
        >
          <DropdownMenuItem
            v-for="status in availabilityStatuses"
            :key="status.value"
            class="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[15px] cursor-pointer outline-none hover:bg-n-alpha-2 hover:text-n-slate-12"
            :class="{
              'bg-n-blue-3/60 text-n-blue-11 font-medium': status.active,
            }"
            @click.stop="changeAvailabilityStatus(status.value)"
          >
            <component :is="status.icon" />
            <span>{{ status.label }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>

    <!-- Auto Offline Toggle -->
    <button
      class="flex items-center justify-between w-full rounded-md px-2.5 py-2 text-[15px] hover:bg-n-alpha-2 cursor-pointer"
      @click.stop
    >
      <div class="flex-grow flex items-center gap-1.5">
        {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
        <Icon
          v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
          icon="i-lucide-info"
          class="size-[18px] text-n-slate-10"
        />
      </div>
      <ToggleSwitch v-model="autoOfflineToggle" @click.stop />
    </button>
  </div>
</template>
